import React from 'react';
import { cn } from '../../utils/cn';
import { Button } from '../../components/ui/Button';
import { useI18n } from '../../i18n/I18nContext';

export function SectionDivider({ title }: { title: string }) {
  return (
    <div className="relative flex items-center py-6">
      <div className="flex-grow border-t border-slate-200 dark:border-slate-700" />
      <span className="mx-4 flex-shrink-0 rounded-full bg-slate-100 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-slate-600 dark:bg-slate-800 dark:text-slate-300">
        {title}
      </span>
      <div className="flex-grow border-t border-slate-200 dark:border-slate-700" />
    </div>
  );
}

export function Toggle({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-start gap-3 cursor-pointer rounded-lg border border-slate-200 bg-white p-4 transition hover:border-red-200 hover:shadow-sm dark:border-slate-700 dark:bg-slate-900 dark:hover:border-red-800">
      <div
        className={cn(
          'relative mt-0.5 h-5 w-9 flex-shrink-0 rounded-full transition-colors duration-200',
          checked ? 'bg-red-600' : 'bg-slate-300 dark:bg-slate-600'
        )}
      >
        <span
          className={cn(
            'absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform duration-200',
            checked ? 'translate-x-4' : 'translate-x-0'
          )}
        />
        <input
          type="checkbox"
          className="sr-only"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
        />
      </div>
      <div>
        <div className="text-sm font-medium text-slate-800 dark:text-slate-200">{label}</div>
        {description && (
          <div className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{description}</div>
        )}
      </div>
    </label>
  );
}

export function ColorPicker({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex w-full flex-col items-start gap-2 sm:w-auto">
      <span className="text-sm font-medium text-slate-800 dark:text-slate-200">{label}</span>
      <div className="flex w-full items-center gap-3">
        <input
          type="color"
          value={value || '#dc2626'}
          onChange={(e) => onChange(e.target.value)}
          className="h-10 w-10 shrink-0 cursor-pointer rounded-lg border border-slate-200 p-0.5 dark:border-slate-700"
        />
        <input
          type="text"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="#dc2626"
          className="w-full min-w-0 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 shadow-sm outline-none transition focus:border-red-400 focus:ring-2 focus:ring-red-200 sm:w-28 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500"
        />
      </div>
    </div>
  );
}

export function TextArea({
  label,
  value,
  onChange,
  rows = 3,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  rows?: number;
  placeholder?: string;
}) {
  return (
    <label className="block">
      <div className="mb-1.5 text-sm font-medium text-slate-800 dark:text-slate-200">{label}</div>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        placeholder={placeholder}
        className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 shadow-sm outline-none transition focus:border-red-400 focus:ring-2 focus:ring-red-200 resize-y dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500"
      />
    </label>
  );
}

export function Select({
  label,
  description,
  value,
  onChange,
  options,
}: {
  label: string;
  description?: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <label className="block">
      <div className="mb-1.5 text-sm font-medium text-slate-800 dark:text-slate-200">{label}</div>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm outline-none transition focus:border-red-400 focus:ring-2 focus:ring-red-200 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {description && (
        <p className="mt-1.5 text-xs text-slate-500 dark:text-slate-400">{description}</p>
      )}
    </label>
  );
}

export function Toast({ toast }: { toast: { type: 'success' | 'error'; message: string } | null }) {
  if (!toast) return null;
  return (
    <div
      className={cn(
        'fixed top-4 right-3 left-3 z-[70] rounded-lg px-4 py-3 text-sm font-medium shadow-lg transition-all sm:left-auto sm:right-4 sm:max-w-sm',
        toast.type === 'success'
          ? 'bg-emerald-50 text-emerald-800 border border-emerald-200 dark:bg-emerald-950/60 dark:text-emerald-300 dark:border-emerald-800'
          : 'bg-rose-50 text-rose-800 border border-rose-200 dark:bg-rose-950/60 dark:text-rose-300 dark:border-rose-800'
      )}
    >
      {toast.message}
    </div>
  );
}

export function PageHeader({
  title,
  description,
  saving,
  onSave,
}: {
  title: string;
  description: string;
  saving: boolean;
  onSave: () => void;
}) {
  const { t } = useI18n();
  return (
    <div className="flex flex-col gap-3 pb-6 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">{title}</h1>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{description}</p>
      </div>
      <Button className="w-full sm:w-auto" onClick={onSave} disabled={saving}>
        {saving ? t('common.saving') : t('common.saveChanges')}
      </Button>
    </div>
  );
}

export function PageFooter({
  saving,
  onSave,
  onDiscard,
}: {
  saving: boolean;
  onSave: () => void;
  onDiscard: () => void;
}) {
  const { t } = useI18n();
  return (
    <div className="flex flex-col-reverse gap-3 pb-8 pt-6 sm:flex-row sm:items-center sm:justify-end">
      <Button variant="secondary" onClick={onDiscard}>
        {t('common.discardChanges')}
      </Button>
      <Button onClick={onSave} disabled={saving}>
        {saving ? t('common.saving') : t('common.saveChanges')}
      </Button>
    </div>
  );
}

export function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center py-32">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-red-600 border-t-transparent" />
    </div>
  );
}
